/**
 * Task Runner — runs a single DSL task via an SDK agent
 *
 * Responsibilities:
 *   1. Resolve task contract → target agent contract
 *   2. Build the agent prompt from contract + context
 *   3. Apply plugin prompt enhancers and beforeTask hooks
 *   4. Send to SDK agent (via abstract SdkAdapter) and stream result
 *   5. Parse + validate result against handoff schema
 *   6. Apply plugin afterTask hooks
 *   7. Return typed result or escalation signal
 *
 * Two recovery strategies:
 *   - followUp: lightweight, same session — fix output format only
 *   - retry:    heavyweight, new session — re-execute entire task
 */

import { z } from "zod";
import * as yaml from "yaml";

import type { MemoryRef } from "./memory-store.js";
import { pluginRegistry } from "./plugin.js";
import { buildCandidateAgents, renderAgentSystemPrompt, type CandidateAgent } from "./candidate-agents.js";

export type { MemoryRef } from "./memory-store.js";
export type { CandidateAgent } from "./candidate-agents.js";
export { buildCandidateAgents, renderAgentSystemPrompt } from "./candidate-agents.js";

// Duck-typed contracts so the runtime doesn't depend on generated shapes.

export interface AgentContractLike {
  readonly id: string;
  readonly role_name: string;
  readonly purpose: string;
  readonly mode: "read-write" | "read-only";
  readonly responsibilities: readonly string[];
  readonly constraints: readonly string[];
  readonly rules?: readonly { readonly id: string; readonly description: string; readonly severity: string }[];
  /**
   * Agent ids this agent is allowed to delegate to. When present, it restricts
   * the candidate sub-agent set registered with the SDK for LLM-internal routing.
   */
  readonly can_invoke_agents?: readonly string[];
}

export interface TaskContractLike {
  readonly id: string;
  readonly description: string;
  readonly target_agent: string;
  readonly result_handoff: string;
  readonly completion_criteria: readonly string[];
  readonly model_class?: "fast" | "standard" | "thinking";
}

// ---------------------------------------------------------------------------
// Progress events
// ---------------------------------------------------------------------------

/**
 * Progress event emitted by adapters during agent execution.
 * Enables real-time visibility into what the agent is doing
 * (tool calls, text output, status changes).
 */
export interface AgentProgressEvent {
  type: "tool_use" | "tool_result" | "text" | "status" | "cache_metrics";
  tool_name?: string;
  /** Tool invocation arguments (e.g. file path, command). */
  input?: Record<string, unknown>;
  message?: string;
  /** SDK session ID for correlating events within a single adapter call. */
  session_id?: string;
  /** Cache utilization metrics from the SDK response. */
  cacheMetrics?: {
    cachedInputTokens?: number;
    totalInputTokens?: number;
  };
}

// ---------------------------------------------------------------------------
// Prompt caching
// ---------------------------------------------------------------------------

/**
 * A prompt split into static (cacheable) and dynamic (per-invocation) sections.
 * Adapters that support prompt caching can use the `system` sections as
 * provider-specific system prompts, reducing token costs on repeated calls.
 */
export interface SplitPrompt {
  /** Static sections: role, responsibilities, constraints, rules, task, schema, recovery. */
  system: string[];
  /** Dynamic sections: user request, handoff input, prior context, files, acceptance criteria. */
  user: string;
}

/**
 * Optional cache configuration for adapters that support prompt caching.
 * Provider-specific defaults apply when individual fields are omitted.
 */
export interface CacheConfig {
  /** Enable prompt caching. Defaults to true when the adapter supports it. */
  enabled?: boolean;
  /** Provider-specific TTL (e.g. "5m", "1h", "24h"). */
  ttl?: string;
  /** Stable key for cross-session cache reuse (provider-specific). */
  cacheKey?: string;
}

// ---------------------------------------------------------------------------
// SDK adapter interface
// ---------------------------------------------------------------------------

/**
 * Base options for SdkAdapter.send(). SDK-specific options (model, cwd,
 * sessionMode, etc.) belong on the adapter constructor / factory — not here.
 */
export interface AdapterSendOptions {
  readonly: boolean;
  /**
   * Optional callback invoked with intermediate progress events during execution.
   * Adapters that consume an internal event stream (e.g. Claude SDK's AsyncGenerator)
   * call this for each non-result message, enabling callers to display live progress.
   */
  onProgress?: (event: AgentProgressEvent) => void;
  /**
   * Split prompt for prompt caching support. When provided, adapters that support
   * caching can separate static (cacheable) instructions from dynamic content.
   * Adapters without caching support can ignore this and use the prompt argument.
   */
  splitPrompt?: SplitPrompt;
  /**
   * Candidate sub-agents the entry agent's LLM may delegate to (model B,
   * LLM-internal routing). Adapters register these via their native subagent
   * mechanism (Claude `agents`, OpenAI `handoffs`, ADK `subAgents`). When
   * omitted or empty, adapters behave exactly as before (single agent).
   */
  agents?: CandidateAgent[];
}

/**
 * Rich execution request for adapters that need more than a prompt string.
 * Adapters can implement sendExecution() to receive full contract context.
 */
export interface AgentExecutionRequest {
  agentId: string;
  taskId: string;
  prompt: string;

  handoff?: {
    type: string;
    payload: unknown;
  };

  schema?: {
    handoffType: string;
    zodSchema: unknown;
    promptDescription: string;
  };

  context: TaskContext;

  /** Resumable memory reference from a previous run. */
  memoryRef?: MemoryRef;

  /**
   * Split prompt for prompt caching support. When provided, adapters can
   * separate static instructions (system) from dynamic content (user).
   */
  splitPrompt?: SplitPrompt;

  /**
   * Candidate sub-agents for LLM-internal routing (model B). Mirrors
   * `AdapterSendOptions.agents`; also forwarded via `options.agents`.
   */
  agents?: CandidateAgent[];

  options: AdapterSendOptions & {
    maxFollowUps: number;
    attempt: number;
  };
}

export interface SdkAdapter {
  /** Start a new task session and return the full text response. */
  send(prompt: string, options: AdapterSendOptions): Promise<string>;

  /**
   * Send a follow-up message within the same agent session.
   * Used for output-format corrections without re-executing the task.
   * Falls back to send() if not implemented (stateless adapters like mock).
   */
  followUp?(message: string): Promise<string>;

  /**
   * Rich execution path. When implemented, the runtime prefers this over
   * send() and passes the full contract context. Adapters that only need
   * a prompt string can ignore this — the runtime falls back to send().
   */
  sendExecution?(request: AgentExecutionRequest): Promise<string>;

  /** Return the memory_ref produced by the last send/sendExecution call. */
  getLastMemoryRef?(): MemoryRef | null;

  /** Check if this adapter is compatible with the given compat tag. */
  isCompatible?(compat: string): boolean;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TaskContext {
  user_request: string;
  prior_context?: string;
  relevant_paths?: string[];
  acceptance_criteria?: string[];

  /**
   * Structured handoff input (primary data). When present, prompt builders
   * should derive the prompt from this rather than user_request alone.
   */
  handoff_input?: {
    type: string;
    version?: number;
    payload: unknown;
  };

  /** Arbitrary structured variables plugins or workflow context may inject. */
  variables?: Record<string, unknown>;

  /** Resumable memory reference from a previous run. */
  memoryRef?: MemoryRef;
}

export type TaskOutcome =
  | { status: "success"; data: unknown; raw: string; memoryRef?: MemoryRef }
  | { status: "validation_error"; errors: z.ZodError; raw: string }
  | { status: "escalation"; reason: string; raw: string }
  | { status: "error"; message: string };

/**
 * Retry strategy decision.
 *
 * - "follow_up": lightweight — same session, fix output format only
 * - "retry":     heavyweight — new send(), re-execute the entire task
 * - "abort":     stop retrying, return the current outcome
 */
export type RetryDecision = "follow_up" | "retry" | "abort";

export interface TaskRunOptions {
  /** Max follow-up attempts (output format correction, same session). Default: 2 */
  maxFollowUps?: number;
  /** Max full retry attempts (re-execute entire task via send). Default: 0 */
  maxRetries?: number;
  /**
   * Called on each failure to decide which recovery strategy to use.
   * Default: validation_error → follow_up, empty output → retry, else abort.
   */
  decideRetryStrategy?: (
    outcome: TaskOutcome,
    counts: { followUps: number; retries: number },
  ) => Promise<RetryDecision> | RetryDecision;
  /**
   * Writable stream for real-time progress output during agent execution.
   * When provided, intermediate adapter events (tool use, status, etc.)
   * are formatted and written here. Typical value: process.stderr.
   */
  progressOutput?: { write(chunk: string): unknown };
}

export interface TaskRunResult {
  outcome: TaskOutcome;
  follow_ups_used: number;
  retries_used: number;
}

// ---------------------------------------------------------------------------
// Schema-to-prompt description utility
// ---------------------------------------------------------------------------

interface FieldDescriptor {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  constraints?: string;
}

type ZodCheckEntry = {
  kind?: string;
  value?: number;
  regex?: RegExp;
  isInt?: boolean;
  _zod?: { def?: Record<string, unknown> };
};

function getZodChecks(schema: z.ZodType): ZodCheckEntry[] {
  const def = (schema as z.ZodType & { _def?: { checks?: ZodCheckEntry[] } })._def;
  return def?.checks ?? [];
}

/** Extract validation constraints from Zod's internal checks for prompt display. */
function extractZodConstraints(schema: z.ZodType): string | undefined {
  const inner = unwrapOptional(schema);
  const parts: string[] = [];

  if (inner instanceof z.ZodString) {
    const str = inner as z.ZodString & {
      minLength?: number | null;
      maxLength?: number | null;
    };
    if (str.minLength != null) parts.push(`min: ${str.minLength}`);
    if (str.maxLength != null) parts.push(`max: ${str.maxLength}`);
    for (const check of getZodChecks(inner)) {
      if (check.kind === "min") parts.push(`min: ${check.value}`);
      if (check.kind === "max") parts.push(`max: ${check.value}`);
      if (check.kind === "regex" && check.regex) parts.push(`regex: ${check.regex}`);
      const def = check._zod?.def;
      if (def?.format === "regex" && def.pattern instanceof RegExp) {
        parts.push(`regex: ${def.pattern}`);
      }
    }
  } else if (inner instanceof z.ZodNumber) {
    let hasInt = false;
    for (const check of getZodChecks(inner)) {
      if (check.kind === "int") hasInt = true;
      if (check.kind === "min") parts.push(`min: ${check.value}`);
      if (check.kind === "max") parts.push(`max: ${check.value}`);
      if (check.isInt) hasInt = true;
      const def = check._zod?.def;
      if (def?.check === "number_format" && def.format === "safeint") hasInt = true;
      if (def?.check === "greater_than") parts.push(`min: ${def.value}`);
      if (def?.check === "less_than") parts.push(`max: ${def.value}`);
    }
    if (hasInt) parts.unshift("int");
  } else if (inner instanceof z.ZodArray) {
    for (const check of getZodChecks(inner)) {
      if (check.kind === "min") parts.push(`min: ${check.value}`);
      if (check.kind === "max") parts.push(`max: ${check.value}`);
      const def = check._zod?.def;
      if (def?.check === "min_length") parts.push(`min: ${def.minimum}`);
      if (def?.check === "max_length") parts.push(`max: ${def.maximum}`);
    }
  }

  return parts.length > 0 ? parts.join(", ") : undefined;
}

function zodTypeToString(schema: z.ZodType): string {
  if (schema instanceof z.ZodString) return "string";
  if (schema instanceof z.ZodNumber) return "number";
  if (schema instanceof z.ZodBoolean) return "boolean";
  if (schema instanceof z.ZodEnum) return `enum(${(schema.options as string[]).join(", ")})`;
  if (schema instanceof z.ZodLiteral) return `"${String((schema._def as { values: unknown[] }).values[0])}"`;

  if (schema instanceof z.ZodArray) {
    const element = (schema as z.ZodArray<z.ZodType>)._def.element;
    const inner = zodTypeToString(element);
    return `${inner}[]`;
  }
  if (schema instanceof z.ZodObject) return "object";
  if (schema instanceof z.ZodOptional) {
    return zodTypeToString((schema as z.ZodOptional<z.ZodType>)._def.innerType);
  }
  if (schema instanceof z.ZodRecord) return "Record<string, unknown>";
  return "unknown";
}

/** Unwrap ZodOptional to get the inner type. */
function unwrapOptional(schema: z.ZodType): z.ZodType {
  if (schema instanceof z.ZodOptional) {
    return (schema as z.ZodOptional<z.ZodType>)._def.innerType;
  }
  return schema;
}

/** Get the element schema from an array type. */
function getArrayElement(schema: z.ZodType): z.ZodType | null {
  const inner = unwrapOptional(schema);
  if (inner instanceof z.ZodArray) {
    return (inner as z.ZodArray<z.ZodType>)._def.element;
  }
  return null;
}

function extractFieldDescriptors(schema: z.ZodType): FieldDescriptor[] | null {
  let shape: Record<string, z.ZodType> | null = null;

  if (schema instanceof z.ZodObject) {
    shape = (schema as z.ZodObject<Record<string, z.ZodType>>).shape;
  }

  if (!shape) return null;

  return Object.entries(shape).map(([name, field]) => {
    const isOptional = field instanceof z.ZodOptional;
    const innerField = isOptional ? (field as z.ZodOptional<z.ZodType>)._def.innerType : field;
    const description = (innerField as z.ZodType & { description?: string }).description
      ?? (field as z.ZodType & { description?: string }).description;
    return {
      name,
      type: zodTypeToString(field as z.ZodType),
      required: !isOptional,
      description,
      constraints: extractZodConstraints(innerField as z.ZodType),
    };
  });
}

/**
 * Convert a Zod schema into a human-readable field table for inclusion in LLM prompts.
 * Recursively generates sub-tables for nested objects and array elements.
 */
export function zodSchemaToPromptDescription(schema: z.ZodType): string {
  const tables: string[] = [];
  collectFieldTables(schema, "", tables);
  return tables.join("\n\n");
}

function collectFieldTables(
  schema: z.ZodType,
  pathPrefix: string,
  tables: string[],
): void {
  const fields = extractFieldDescriptors(schema);
  if (!fields || fields.length === 0) return;

  const title = pathPrefix ? `**${pathPrefix}**\n\n` : "";
  const header = "| Field | Type | Required | Constraints | Description |";
  const sep = "|-------|------|----------|-------------|-------------|";
  const rows = fields.map((f) =>
    `| ${f.name} | ${f.type} | ${f.required ? "Yes" : "No"} | ${f.constraints ?? ""} | ${f.description ?? ""} |`,
  );
  tables.push(`${title}${[header, sep, ...rows].join("\n")}`);

  const shape = (schema instanceof z.ZodObject)
    ? (schema as z.ZodObject<Record<string, z.ZodType>>).shape
    : null;
  if (!shape) return;

  for (const f of fields) {
    const rawField = shape[f.name];
    const inner = unwrapOptional(rawField);

    if (inner instanceof z.ZodObject) {
      collectFieldTables(inner, pathPrefix ? `${pathPrefix}.${f.name}` : f.name, tables);
    } else if (inner instanceof z.ZodArray) {
      const element = (inner as z.ZodArray<z.ZodType>)._def.element;
      if (element instanceof z.ZodObject) {
        collectFieldTables(element, pathPrefix ? `${pathPrefix}.${f.name}[]` : `${f.name}[]`, tables);
      }
    }
  }
}

/**
 * Generate a YAML example from a Zod schema shape, recursively expanding
 * nested objects and arrays up to a configurable depth.
 */
export function zodSchemaToYamlExample(schema: z.ZodType, maxDepth = 3): string {
  return renderYamlObject(schema, 0, maxDepth);
}

function renderYamlObject(schema: z.ZodType, indent: number, maxDepth: number): string {
  const fields = extractFieldDescriptors(schema);
  if (!fields || fields.length === 0) return "";

  const prefix = "  ".repeat(indent);
  const lines: string[] = [];

  for (const f of fields) {
    const shape = (schema instanceof z.ZodObject)
      ? (schema as z.ZodObject<Record<string, z.ZodType>>).shape
      : null;
    const rawField = shape?.[f.name];

    if (f.type.endsWith("[]")) {
      lines.push(`${prefix}${f.name}:`);
      const element = rawField ? getArrayElement(rawField) : null;
      if (element && element instanceof z.ZodObject && indent < maxDepth) {
        const nested = renderYamlObject(element, indent + 2, maxDepth);
        if (nested) {
          const basePrefix = "  ".repeat(indent + 2);
          const nestedLines = nested.split("\n");
          lines.push(`${prefix}  - ${nestedLines[0].slice(basePrefix.length)}`);
          for (let i = 1; i < nestedLines.length; i++) {
            lines.push(`${prefix}    ${nestedLines[i].slice(basePrefix.length)}`);
          }
        } else {
          lines.push(`${prefix}  - key: "value"`);
        }
      } else if (f.type === "string[]") {
        lines.push(`${prefix}  - "example"`);
      } else {
        lines.push(`${prefix}  - key: "value"`);
      }
    } else if (f.type === "object" && indent < maxDepth) {
      const inner = rawField ? unwrapOptional(rawField) : null;
      if (inner && inner instanceof z.ZodObject) {
        lines.push(`${prefix}${f.name}:`);
        const nested = renderYamlObject(inner, indent + 1, maxDepth);
        if (nested) {
          lines.push(nested);
        } else {
          lines.push(`${prefix}  key: "value"`);
        }
      } else {
        lines.push(`${prefix}${f.name}:`);
        lines.push(`${prefix}  key: "value"`);
      }
    } else if (f.type === "string") {
      lines.push(`${prefix}${f.name}: "..."`);
    } else if (f.type === "number") {
      lines.push(`${prefix}${f.name}: 0`);
    } else if (f.type === "boolean") {
      lines.push(`${prefix}${f.name}: false`);
    } else if (f.type.startsWith("enum(")) {
      const enumVals = f.type.slice(5, -1).split(", ");
      lines.push(`${prefix}${f.name}: "${enumVals[0]}"`);
    } else {
      lines.push(`${prefix}${f.name}: "..."`);
    }
  }
  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Prompt builder
// ---------------------------------------------------------------------------

export interface PromptBuilderOptions {
  handoffSchemas?: Record<string, z.ZodType>;
  retry?: {
    condition: string;
    fix_task: string;
    revalidate_task?: string;
  };
}

/**
 * Build a prompt split into static (cacheable) and dynamic (per-invocation) parts.
 *
 * Static (system): Role, Responsibilities, Constraints, Rules, Task description,
 * Completion Criteria, Output Format / Schema, Recovery Instructions.
 *
 * Dynamic (user): User Request, Handoff Input, Prior Context,
 * Relevant Files, Acceptance Criteria.
 */
export function buildSplitTaskPrompt(
  agent: AgentContractLike,
  task: TaskContractLike,
  context: TaskContext,
  options?: PromptBuilderOptions,
): SplitPrompt {
  const system: string[] = [];

  // Role / Responsibilities / Constraints / Rules — shared with candidate
  // sub-agent prompts via renderAgentSystemPrompt so both stay in sync.
  system.push(renderAgentSystemPrompt(agent));

  system.push(`## Task: ${task.id}\n\n${task.description}`);

  if (task.completion_criteria.length > 0) {
    system.push(
      `## Completion Criteria\n\n${task.completion_criteria.map((c) => `- ${c}`).join("\n")}`,
    );
  }

  const handoffId = task.result_handoff;
  const schema = options?.handoffSchemas?.[handoffId];
  let outputSection = `## Required Output Format\n\nReturn your result as a YAML block matching the "${handoffId}" schema.`;

  if (schema) {
    const fieldTable = zodSchemaToPromptDescription(schema);
    if (fieldTable) {
      outputSection += `\n\n${fieldTable}`;
    }

    const yamlExample = zodSchemaToYamlExample(schema);
    if (yamlExample) {
      outputSection += `\n\nExample:\n\n\`\`\`yaml\n${yamlExample}\n\`\`\``;
    }
  } else {
    outputSection += ` Include all required fields.`;
  }

  system.push(outputSection);

  if (options?.retry) {
    const r = options.retry;
    let recoverySection = `## Recovery Instructions\n\nIf the task fails due to: "${r.condition}"\n→ Apply the recovery approach from task "${r.fix_task}"`;
    if (r.revalidate_task) {
      recoverySection += `\n→ Then revalidate using task "${r.revalidate_task}"`;
    }
    system.push(recoverySection);
  }

  // Dynamic sections (per-invocation)
  const userSections: string[] = [];

  if (context.handoff_input) {
    const hi = context.handoff_input;
    const payloadStr = typeof hi.payload === "string"
      ? hi.payload
      : yaml.stringify(hi.payload);
    userSections.push(`## User Request\n\n${context.user_request}`);
    userSections.push(`## Handoff Input (${hi.type})\n\n\`\`\`yaml\n${payloadStr}\`\`\``);
  } else {
    userSections.push(`## User Request\n\n${context.user_request}`);
  }

  if (context.prior_context) {
    userSections.push(`## Prior Context\n\n${context.prior_context}`);
  }

  if (context.relevant_paths && context.relevant_paths.length > 0) {
    userSections.push(
      `## Relevant Files\n\n${context.relevant_paths.map((p) => `- ${p}`).join("\n")}`,
    );
  }

  if (context.acceptance_criteria && context.acceptance_criteria.length > 0) {
    userSections.push(
      `## Acceptance Criteria\n\n${context.acceptance_criteria.map((c) => `- ${c}`).join("\n")}`,
    );
  }

  return {
    system,
    user: userSections.join("\n\n---\n\n"),
  };
}

/** Join a SplitPrompt back into a single monolithic prompt string. */
function joinSplitPrompt(split: SplitPrompt): string {
  return [...split.system, split.user].join("\n\n---\n\n");
}

export function buildTaskPrompt(
  agent: AgentContractLike,
  task: TaskContractLike,
  context: TaskContext,
  options?: PromptBuilderOptions,
): string {
  return joinSplitPrompt(buildSplitTaskPrompt(agent, task, context, options));
}

// ---------------------------------------------------------------------------
// Result parser
// ---------------------------------------------------------------------------

export function extractStructuredResult(raw: string): unknown | null {
  const yamlMatch = raw.match(/```ya?ml\n([\s\S]*?)```/);
  if (yamlMatch) {
    try { return yaml.parse(yamlMatch[1]); } catch { /* fall through */ }
  }

  const jsonMatch = raw.match(/```json\n([\s\S]*?)```/);
  if (jsonMatch) {
    try { return JSON.parse(jsonMatch[1]); } catch { /* fall through */ }
  }

  try { return JSON.parse(raw); } catch { /* not JSON */ }
  return null;
}

// ---------------------------------------------------------------------------
// Internal: validate LLM output against handoff schema
// ---------------------------------------------------------------------------

function validateOutput(
  fullOutput: string,
  task: TaskContractLike,
  schemas: Record<string, z.ZodType>,
): TaskOutcome {
  if (!fullOutput.trim()) {
    return { status: "error", message: "Agent returned empty output" };
  }

  const parsed = extractStructuredResult(fullOutput);
  if (!parsed) {
    return {
      status: "validation_error",
      errors: new z.ZodError([{ code: "custom", message: "Could not extract structured result from agent output", path: [] }]),
      raw: fullOutput,
    };
  }

  const schema = schemas[task.result_handoff];
  if (!schema) {
    return { status: "success", data: parsed, raw: fullOutput };
  }

  const validation = schema.safeParse(parsed);
  if (!validation.success) {
    return { status: "validation_error", errors: validation.error, raw: fullOutput };
  }

  const data = validation.data as Record<string, unknown>;
  if (data.escalation_needed === true) {
    return {
      status: "escalation",
      reason: (data.escalation_reasons as string) ?? "Agent requested escalation",
      raw: fullOutput,
    };
  }

  return { status: "success", data: validation.data, raw: fullOutput };
}

function attachMemoryRef(outcome: TaskOutcome, adapter: SdkAdapter): TaskOutcome {
  if (outcome.status !== "success") return outcome;
  const ref = adapter.getLastMemoryRef?.();
  if (!ref) return outcome;
  return { ...outcome, memoryRef: ref };
}

// ---------------------------------------------------------------------------
// Follow-up prompt builder (lightweight, output-format correction only)
// ---------------------------------------------------------------------------

export function buildRetryFollowUp(
  task: TaskContractLike,
  lastOutcome: TaskOutcome,
  options?: PromptBuilderOptions,
): string {
  const sections: string[] = [];
  sections.push(
    "Your previous response could not be validated. " +
    "The task work itself is fine — please re-output only the structured result block in the correct format.",
  );

  if (lastOutcome.status === "validation_error") {
    sections.push(`Validation errors:\n${lastOutcome.errors.message}`);
  } else if (lastOutcome.status === "error") {
    sections.push(`Error: ${lastOutcome.message}`);
  }

  const handoffId = task.result_handoff;
  const schema = options?.handoffSchemas?.[handoffId];
  let formatSection = `Required format: Return your result as a YAML block matching the "${handoffId}" schema.`;

  if (schema) {
    const fieldTable = zodSchemaToPromptDescription(schema);
    if (fieldTable) {
      formatSection += `\n\n${fieldTable}`;
    }

    const yamlExample = zodSchemaToYamlExample(schema);
    if (yamlExample) {
      formatSection += `\n\nExample:\n\n\`\`\`yaml\n${yamlExample}\n\`\`\``;
    }
  } else {
    formatSection += ` Include all required fields.`;
  }

  sections.push(formatSection);

  return sections.join("\n\n");
}

// ---------------------------------------------------------------------------
// Default retry strategy
// ---------------------------------------------------------------------------

function defaultDecideRetryStrategy(
  outcome: TaskOutcome,
  _counts: { followUps: number; retries: number },
): RetryDecision {
  if (outcome.status === "validation_error") {
    return "follow_up";
  }
  if (outcome.status === "error") {
    if (outcome.message === "Agent returned empty output") return "retry";
    return "abort";
  }
  return "abort";
}

// ---------------------------------------------------------------------------
// Progress formatting
// ---------------------------------------------------------------------------

function summarizeToolInput(input?: Record<string, unknown>): string {
  if (!input) return "";
  const path = input.file_path ?? input.path ?? input.filename ?? input.file;
  if (typeof path === "string") return ` — ${path}`;
  const cmd = input.command ?? input.cmd;
  if (typeof cmd === "string") return ` — ${cmd.length > 80 ? cmd.slice(0, 77) + "..." : cmd}`;
  const query = input.query ?? input.search ?? input.pattern;
  if (typeof query === "string") return ` — "${query}"`;
  return "";
}

function timestamp(): string {
  return new Date().toISOString();
}

function formatProgressPrefix(
  event: AgentProgressEvent,
  taskId: string,
  agentId?: string,
): string {
  const ts = timestamp();
  const sid = event.session_id ? event.session_id.slice(0, 8) : "-";
  const agent = agentId ?? "-";
  return `[${ts},${sid},${agent},${taskId}]`;
}

function formatProgressEvent(
  event: AgentProgressEvent,
  taskId: string,
  agentId?: string,
): string {
  const prefix = formatProgressPrefix(event, taskId, agentId);
  switch (event.type) {
    case "tool_use":
      return `${prefix} ⚙ ${event.tool_name ?? "tool"}${summarizeToolInput(event.input)}\n`;
    case "tool_result":
      return "";
    case "text": {
      if (!event.message) return "";
      const trimmed = event.message.trim();
      if (!trimmed) return "";
      const firstLine = trimmed.split("\n")[0];
      const preview = firstLine.length > 120 ? firstLine.slice(0, 117) + "..." : firstLine;
      return `${prefix} 💭 ${preview}\n`;
    }
    case "status":
      return event.message ? `${prefix} ● ${event.message}\n` : "";
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

export async function runTask(
  adapter: SdkAdapter,
  taskId: string,
  context: TaskContext,
  options?: TaskRunOptions & {
    agentRegistry?: Record<string, AgentContractLike>;
    taskRegistry?: Record<string, TaskContractLike>;
    handoffSchemas?: Record<string, z.ZodType>;
    retry?: { condition: string; fix_task: string; revalidate_task?: string };
  },
): Promise<TaskRunResult> {
  const taskReg = options?.taskRegistry ?? {};
  const agentReg = options?.agentRegistry ?? {};
  const schemas = options?.handoffSchemas ?? {};

  const task = taskReg[taskId];
  if (!task) return { outcome: { status: "error", message: `Unknown task: ${taskId}` }, follow_ups_used: 0, retries_used: 0 };

  const agentContract = agentReg[task.target_agent];
  if (!agentContract) return { outcome: { status: "error", message: `Unknown agent: ${task.target_agent}` }, follow_ups_used: 0, retries_used: 0 };

  const modifiedContext = await pluginRegistry.runBeforeTask(taskId, context);
  if (modifiedContext === null) {
    return { outcome: { status: "error", message: `Task "${taskId}" skipped by plugin` }, follow_ups_used: 0, retries_used: 0 };
  }

  const enrichedContext = pluginRegistry.applyContextEnhancers(taskId, modifiedContext);

  const promptOpts: PromptBuilderOptions = { handoffSchemas: schemas, retry: options?.retry };
  const builderArgs = { agent: agentContract, task, context: enrichedContext, options: promptOpts };

  let prompt: string;
  let splitPrompt: SplitPrompt | undefined;

  const pluginPrompt = pluginRegistry.applyPromptBuilder(builderArgs);
  if (pluginPrompt) {
    prompt = pluginPrompt;
  } else {
    const split = buildSplitTaskPrompt(agentContract, task, enrichedContext, promptOpts);
    splitPrompt = split;
    prompt = joinSplitPrompt(split);
  }

  const enhancedPrompt = pluginRegistry.applyPromptEnhancers(taskId, prompt, enrichedContext);
  if (enhancedPrompt !== prompt) {
    splitPrompt = undefined;
    prompt = enhancedPrompt;
  }

  const isReadonly = agentContract.mode === "read-only";

  // Build candidate sub-agents for LLM-internal routing (model B). The entry
  // agent (this task's target_agent) gets every other registry agent as a
  // candidate, restricted to its can_invoke_agents allow-list when present.
  // Empty registry → undefined (adapters then behave exactly as before).
  const candidateAgents = Object.keys(agentReg).length > 0
    ? buildCandidateAgents(agentReg, {
        entryAgentId: agentContract.id,
        allowedAgentIds: agentContract.can_invoke_agents,
      })
    : [];
  const agents = candidateAgents.length > 0 ? candidateAgents : undefined;

  const maxFollowUps = options?.maxFollowUps ?? 2;
  const maxRetries = options?.maxRetries ?? 0;
  const decide = options?.decideRetryStrategy ?? defaultDecideRetryStrategy;

  const onProgress = options?.progressOutput
    ? (event: AgentProgressEvent) => {
        const line = formatProgressEvent(event, taskId, agentContract.id);
        if (line) options.progressOutput!.write(line);
      }
    : undefined;

  let followUpsUsed = 0;
  let retriesUsed = 0;

  let memoryRef = enrichedContext.memoryRef;
  if (memoryRef && adapter.isCompatible && !adapter.isCompatible(memoryRef.compat)) {
    console.warn(
      `Memory ref compat mismatch: ${memoryRef.compat} is not compatible with this adapter`,
    );
    memoryRef = undefined;
  }

  // --- Initial send (prefer sendExecution if available) ---
  let fullOutput: string;
  try {
    if (adapter.sendExecution) {
      fullOutput = await adapter.sendExecution({
        agentId: agentContract.id,
        taskId,
        prompt,
        handoff: schemas[task.result_handoff]
          ? { type: task.result_handoff, payload: undefined }
          : undefined,
        schema: schemas[task.result_handoff]
          ? {
              handoffType: task.result_handoff,
              zodSchema: schemas[task.result_handoff],
              promptDescription: zodSchemaToPromptDescription(schemas[task.result_handoff]),
            }
          : undefined,
        context: enrichedContext,
        memoryRef,
        splitPrompt,
        agents,
        options: { readonly: isReadonly, onProgress, splitPrompt, agents, maxFollowUps, attempt: 0 },
      });
    } else {
      fullOutput = await adapter.send(prompt, { readonly: isReadonly, onProgress, splitPrompt, agents });
    }
  } catch (err) {
    return { outcome: { status: "error", message: `SDK error: ${(err as Error).message}` }, follow_ups_used: 0, retries_used: 0 };
  }

  let outcome = attachMemoryRef(validateOutput(fullOutput, task, schemas), adapter);
  outcome = await pluginRegistry.runAfterTask(taskId, outcome);

  // --- Recovery loop ---
  while (outcome.status === "validation_error" || outcome.status === "error") {
    const decision = await decide(outcome, { followUps: followUpsUsed, retries: retriesUsed });

    if (decision === "abort") break;

    // If requested strategy is exhausted, escalate to the other
    let effectiveDecision = decision;
    if (effectiveDecision === "follow_up" && followUpsUsed >= maxFollowUps) {
      effectiveDecision = "retry";
    }
    if (effectiveDecision === "retry" && retriesUsed >= maxRetries) {
      break;
    }

    if (effectiveDecision === "follow_up") {
      followUpsUsed++;

      const followUpPrompt = buildRetryFollowUp(task, outcome, promptOpts);
      try {
        if (adapter.followUp) {
          fullOutput = await adapter.followUp(followUpPrompt);
        } else {
          const retryContext: TaskContext = {
            ...modifiedContext,
            prior_context: (modifiedContext.prior_context ?? "") +
              `\n\n--- FollowUp ${followUpsUsed} ---\n${followUpPrompt}`,
          };
          let retryFullPrompt = buildTaskPrompt(agentContract, task, retryContext, promptOpts);
          retryFullPrompt = pluginRegistry.applyPromptEnhancers(taskId, retryFullPrompt, retryContext);
          fullOutput = await adapter.send(retryFullPrompt, { readonly: isReadonly, agents });
        }
      } catch (err) {
        outcome = { status: "error", message: `SDK error on followUp ${followUpsUsed}: ${(err as Error).message}` };
        continue;
      }
    } else {
      // effectiveDecision === "retry" — full re-execution
      retriesUsed++;

      const retryContext: TaskContext = {
        ...modifiedContext,
        prior_context: (modifiedContext.prior_context ?? "") +
          `\n\n--- Full Retry ${retriesUsed} ---\nPrevious attempt failed: ` +
          (outcome.status === "validation_error"
            ? `Validation errors: ${outcome.errors.message}`
            : outcome.status === "error" ? outcome.message : "unknown") +
          `\nPlease re-execute the entire task.`,
      };
      let retryFullPrompt = buildTaskPrompt(agentContract, task, retryContext);
      retryFullPrompt = pluginRegistry.applyPromptEnhancers(taskId, retryFullPrompt, retryContext);

      try {
        fullOutput = await adapter.send(retryFullPrompt, { readonly: isReadonly, agents });
      } catch (err) {
        outcome = { status: "error", message: `SDK error on retry ${retriesUsed}: ${(err as Error).message}` };
        continue;
      }
    }

    outcome = attachMemoryRef(validateOutput(fullOutput, task, schemas), adapter);
    outcome = await pluginRegistry.runAfterTask(taskId, outcome);
  }

  return { outcome, follow_ups_used: followUpsUsed, retries_used: retriesUsed };
}
